import ScrollReveal from '@/components/ui/ScrollReveal'

function MUICard() {
  return (
    <div className="bg-surface border border-border rounded-2xl p-6 md:p-8">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-h2 font-bold text-text-primary">MUI</span>
        <span className="text-body-sm text-text-muted">Motion User Interface</span>
      </div>
      <p className="text-body-sm text-text-secondary mb-6">
        High-fidelity video specs showing animations at full speed and slow motion, ensuring
        accurate visual handoff to engineering teams.
      </p>

      <video
        className="w-full rounded-xl"
        src="/videos/vodafone/deliverable-mui.mp4"
        autoPlay
        muted
        loop
        playsInline
      />
    </div>
  )
}

function MDGCard() {
  return (
    <div className="bg-surface border border-border rounded-2xl p-6 md:p-8">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-h2 font-bold text-text-primary">MDG</span>
        <span className="text-body-sm text-text-muted">Motion Developer Guide</span>
      </div>
      <p className="text-body-sm text-text-secondary mb-6">
        Frame-by-frame keyframe data paired with video references, explicitly stating exact start
        times, end times, properties, and easing curves.
      </p>

      <video
        className="w-full rounded-xl"
        src="/videos/vodafone/deliverable-mdg.mp4"
        autoPlay
        muted
        loop
        playsInline
      />
    </div>
  )
}

function MPRCard() {
  return (
    <div className="bg-surface border border-border rounded-2xl p-6 md:p-8 flex flex-col h-full">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-h2 font-bold text-text-primary">MPR</span>
        <span className="text-body-sm text-text-muted">Motion Prototype</span>
      </div>
      <p className="text-body-sm text-text-secondary mb-6">
        Interactive prototypes built in Principle and Figma with embedded narration, bridging the
        conceptual gap between design intent and engineering execution.
      </p>

      <div className="flex-1 flex items-stretch">
        <video
          className="w-full rounded-xl object-cover"
          src="/videos/vodafone/deliverable-mpr.mp4"
          autoPlay
          muted
          loop
          playsInline
        />
      </div>
    </div>
  )
}

function TokenizationPanel() {
  return (
    <div className="bg-surface border border-border rounded-2xl p-6 md:p-8 flex flex-col h-full">
      <h3 className="text-h3 text-text-primary mb-3">Motion Tokens</h3>
      <p className="text-body-sm text-text-secondary mb-4">
        Standardized kinetic values as semantic tokens &mdash; allowing designers and engineers to
        speak the exact same language. Global updates by altering a single token variable.
      </p>
      <div className="bg-surface-hover rounded-xl p-4 md:p-5 overflow-hidden flex-1">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-text-muted/30" />
            <div className="w-2 h-2 rounded-full bg-text-muted/30" />
            <div className="w-2 h-2 rounded-full bg-text-muted/30" />
          </div>
          <div className="flex-1 h-4 bg-surface rounded-md flex items-center px-2">
            <span className="text-[9px] text-text-muted truncate">design-system/tokens/motion</span>
          </div>
        </div>
        <div className="space-y-2">
          {[
            { token: 'motion.duration.instant', value: '100ms' },
            { token: 'motion.duration.fast', value: '200ms' },
            { token: 'motion.duration.medium', value: '333ms' },
            { token: 'motion.duration.slow', value: '500ms' },
            { token: 'motion.duration.xslow', value: '700ms' },
            { token: 'motion.easing.standard', value: 'cubic-bezier(0.4, 0, 0.1, 1)' },
            { token: 'motion.easing.entrance', value: 'cubic-bezier(0, 0, 0.6, 1)' },
            { token: 'motion.easing.exit', value: 'cubic-bezier(0.4, 0, 1, 1)' },
            { token: 'motion.easing.expressive', value: 'cubic-bezier(0.2, 0, 0, 1.4)' },
          ].map((item) => (
            <div key={item.token} className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3 text-caption">
              <code className="text-[#E60000] bg-[#E60000]/5 px-1.5 py-0.5 rounded text-[10px] self-start">
                {item.token}
              </code>
              <code className="text-text-muted text-[10px] pl-0.5 sm:pl-0">{item.value}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function VodafoneHandoff() {
  return (
    <div>
      <ScrollReveal>
        <span className="text-label text-accent block mb-1">02</span>
        <p className="text-label uppercase tracking-widest text-text-muted mb-2">Deliverables</p>
        <h2 className="text-h2 text-text-primary mb-3">From Design to Production</h2>
        <p className="text-body text-text-secondary mb-8 max-w-2xl">
          MUIs (Motion user interfaces), MDGs (Motion developer guides), MPRs (Motion prototypes),
          Lottie &amp; other animated assets.
        </p>
      </ScrollReveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ScrollReveal>
          <MUICard />
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <MDGCard />
        </ScrollReveal>
        <ScrollReveal delay={0.15}>
          <MPRCard />
        </ScrollReveal>
        <ScrollReveal delay={0.2}>
          <TokenizationPanel />
        </ScrollReveal>
      </div>
    </div>
  )
}
